import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Mail, KeyRound, Power, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, NativeSelect, LoadingBlock, Spinner } from "@/components/Shared";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", role: "employee", craft: "Consultant", hire_date: "", password: "", send_invite: false, timesheet_only: false };

function EmployeeDialog({ open, onClose, emp, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(emp ? { ...EMPTY, ...emp, password: "" } : EMPTY); }, [open, emp]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (emp) {
        const { password, send_invite, ...rest } = f;
        await api.put(`/users/${emp.id}`, { ...Object.fromEntries(["first_name", "last_name", "email", "phone", "role", "craft", "hire_date"].map((k) => [k, rest[k] ?? ""])), timesheet_only: f.role === "employee" && !!f.timesheet_only });
        if (password) await api.post(`/users/${emp.id}/set-password`, { password });
        toast.success("Employee updated");
      } else {
        const { data } = await api.post("/users", { ...f, password: f.password || null });
        toast.success(f.send_invite ? (data.invite_sent ? "Employee added — invite email sent" : "Employee added, but the invite email could not be sent") : "Employee added");
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const inp = (k, props = {}) => <input className="field-input" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`employee-${k}-input`} {...props} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white" data-testid="employee-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{emp ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" required>{inp("first_name", { required: true })}</Field>
            <Field label="Last name" required>{inp("last_name", { required: true })}</Field>
          </div>
          <Field label="Email" required>{inp("email", { type: "email", required: true })}</Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">{inp("phone", { type: "tel" })}</Field>
            <Field label="Hire date">{inp("hire_date", { type: "date" })}</Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <NativeSelect value={f.role} onChange={(e) => setF({ ...f, role: e.target.value, timesheet_only: e.target.value === "employee" && f.timesheet_only })} data-testid="employee-role-input">
                <option value="employee">Employee</option><option value="admin">Admin / Owner</option>
              </NativeSelect>
            </Field>
            <Field label="Craft" hint="Prints after name">{inp("craft")}</Field>
          </div>
          {f.role === "employee" && (
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-0.5 h-5 w-5 accent-[#5C241E]" checked={!!f.timesheet_only} onChange={(e) => setF({ ...f, timesheet_only: e.target.checked })} data-testid="employee-timesheet-only-checkbox" /><span><span className="font-semibold">Timesheet only</span><span className="block text-xs text-slate-500">They go straight to their timesheet and don't see daily reports. They can still be picked as crew.</span></span></label>
          )}
          <Field label={emp ? "Set new password (optional)" : "Temporary password"} hint="At least 8 characters">{inp("password", { type: "text", autoComplete: "new-password" })}</Field>
          {!emp && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-5 w-5 accent-[#5C241E]" checked={f.send_invite} onChange={(e) => setF({ ...f, send_invite: e.target.checked })} data-testid="employee-send-invite-checkbox" />Email a set-up link so they choose their own password</label>
          )}
          <button className="btn-brand h-12 w-full" disabled={busy} data-testid="employee-save-button">{busy && <Spinner className="text-white" />}Save employee</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Team" title="Employees" actions={<button className="btn-brand h-11" onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-employee-button"><Plus className="h-4 w-4" />Add employee</button>} />
      {!list ? <LoadingBlock /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm" data-testid="employees-table">
            <thead><tr className="border-b border-border bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Email / phone</th><th className="px-4 py-3">Role</th><th className="px-4 py-3 text-right">Reports</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} className={`border-b border-border last:border-0 ${u.active ? "" : "bg-slate-50 text-slate-500"}`} data-testid={`employee-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold">{u.name}<span className="block text-xs font-normal text-slate-500">{u.craft}</span></td>
                  <td className="px-4 py-3">{u.email}<span className="block text-xs text-slate-500">{u.phone}</span></td>
                  <td className="px-4 py-3 capitalize">{u.role === "admin" ? "Admin" : u.timesheet_only ? <span data-testid={`employee-timesheet-only-${u.id}`}>Employee <span className="block text-xs normal-case text-slate-500">Timesheet only</span></span> : "Employee"}</td>
                  <td className="px-4 py-3 text-right font-mono-num"><Link to={`/admin/reports?employee_id=${u.id}`} className="hover:underline" data-testid={`employee-reports-${u.id}`}>{u.report_count}</Link></td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase ${u.active ? "text-emerald-700" : "text-slate-500"}`} data-testid={`employee-status-${u.id}`}>{u.active ? "Active" : "Disabled"}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button className="btn-ghost h-9 px-2.5" title="Edit" onClick={() => { setEdit(u); setOpen(true); }} data-testid={`employee-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button className="btn-ghost h-9 px-2.5" title="Email password reset" onClick={() => act(() => api.post(`/users/${u.id}/send-reset`), "Reset email sent")} data-testid={`employee-reset-${u.id}`}><Mail className="h-3.5 w-3.5" /></button>
                      <Link className="btn-ghost h-9 px-2.5" title="Report history" to={`/admin/reports?employee_id=${u.id}`} data-testid={`employee-history-${u.id}`}><FileText className="h-3.5 w-3.5" /></Link>
                      {u.id !== user.id && (
                        <button className="btn-ghost h-9 px-2.5" title={u.active ? "Deactivate" : "Activate"} onClick={() => act(() => api.put(`/users/${u.id}`, { active: !u.active }), u.active ? "Employee deactivated" : "Employee activated")} data-testid={`employee-toggle-active-${u.id}`}>
                          <Power className={`h-3.5 w-3.5 ${u.active ? "text-red-600" : "text-emerald-600"}`} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 flex items-center gap-1 text-xs text-slate-600"><KeyRound className="h-3 w-3" />Employees with reports are deactivated, never deleted, so their history stays intact.</p>
      <EmployeeDialog open={open} onClose={() => setOpen(false)} emp={edit} onSaved={load} />
    </div>
  );
}
